import { PERMISSIONS } from '@ibe/constants';
import { Button, Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { type PropertySetupFormInput, type PropertySetupResponse } from '../api/schemas.js';
import {
  CreateAmenityDialog,
  CreateNotificationGatewayDialog,
  CreatePaymentGatewayDialog,
} from './property-setup-create-dialogs.js';

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface PropertySetupFieldsProps {
  readonly form: UseFormReturn<PropertySetupFormInput>;
  readonly emailGatewayOptions: readonly SelectOption[];
  readonly smsGatewayOptions: readonly SelectOption[];
  readonly paymentGatewayOptions: readonly SelectOption[];
  readonly amenities: PropertySetupResponse['amenities'];
}

export function PropertySetupFields({
  form,
  emailGatewayOptions,
  smsGatewayOptions,
  paymentGatewayOptions,
  amenities,
}: PropertySetupFieldsProps): React.JSX.Element {
  const { control, formState, register, watch } = form;

  const [emailAdditions, setEmailAdditions] = useState<readonly SelectOption[]>([]);
  const [smsAdditions, setSmsAdditions] = useState<readonly SelectOption[]>([]);
  const [paymentAdditions, setPaymentAdditions] = useState<readonly SelectOption[]>([]);
  const [amenityAdditions, setAmenityAdditions] = useState<PropertySetupResponse['amenities']>([]);

  const dedupe = (
    base: readonly SelectOption[],
    additions: readonly SelectOption[],
  ): readonly SelectOption[] => {
    const seen = new Set(base.map((option) => option.value));
    return [...base, ...additions.filter((option) => !seen.has(option.value))];
  };

  const dedupeAmenities = (
    base: PropertySetupResponse['amenities'],
    additions: PropertySetupResponse['amenities'],
  ): PropertySetupResponse['amenities'] => {
    const seen = new Set(base.map((amenity) => amenity.amenityId));
    return [...base, ...additions.filter((amenity) => !seen.has(amenity.amenityId))];
  };

  const emails = useMemo(
    () => dedupe(emailGatewayOptions, emailAdditions),
    [emailGatewayOptions, emailAdditions],
  );
  const sms = useMemo(
    () => dedupe(smsGatewayOptions, smsAdditions),
    [smsGatewayOptions, smsAdditions],
  );
  const payments = useMemo(
    () => dedupe(paymentGatewayOptions, paymentAdditions),
    [paymentGatewayOptions, paymentAdditions],
  );
  const amenityList = useMemo(
    () => dedupeAmenities(amenities, amenityAdditions),
    [amenities, amenityAdditions],
  );

  const gatewayPlaceholderOptions = [{ value: '', label: 'Not set' }] as const;

  return (
    <>
      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Section 02
          </p>
          <h2 className="text-lg font-semibold">Notification gateways</h2>
          <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
            <CreateNotificationGatewayDialog
              onCreated={(gateway) => {
                const option = { value: gateway.id, label: gateway.name };
                if (gateway.channel === 'EMAIL') {
                  setEmailAdditions((prev) => [...prev, option]);
                  form.setValue('smtpGatewayId', gateway.id);
                } else {
                  setSmsAdditions((prev) => [...prev, option]);
                  form.setValue('smsGatewayId', gateway.id);
                }
              }}
            >
              <Button variant="outline">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New notification gateway
              </Button>
            </CreateNotificationGatewayDialog>
          </PermissionGuard>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field error={formState.errors.smtpGatewayId?.message}>
            <FieldLabel>Email (SMTP) gateway</FieldLabel>
            <Controller
              control={control}
              name="smtpGatewayId"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={[...gatewayPlaceholderOptions, ...emails]}
                  placeholder="Select an email gateway"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={formState.errors.smsGatewayId?.message}>
            <FieldLabel>SMS gateway</FieldLabel>
            <Controller
              control={control}
              name="smsGatewayId"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={[...gatewayPlaceholderOptions, ...sms]}
                  placeholder="Select an SMS gateway"
                  searchable
                />
              )}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Section 03
          </p>
          <h2 className="text-lg font-semibold">Payment</h2>
          <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE}>
            <CreatePaymentGatewayDialog
              onCreated={(gateway) => {
                setPaymentAdditions((prev) => [
                  ...prev,
                  { value: gateway.id, label: gateway.name },
                ]);
                form.setValue('paymentGatewayId', gateway.id);
              }}
            >
              <Button variant="outline">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New payment gateway
              </Button>
            </CreatePaymentGatewayDialog>
          </PermissionGuard>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field error={formState.errors.paymentGatewayId?.message}>
            <FieldLabel>Payment gateway</FieldLabel>
            <Controller
              control={control}
              name="paymentGatewayId"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={[...gatewayPlaceholderOptions, ...payments]}
                  placeholder="Select a payment gateway"
                  searchable
                />
              )}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Section 04
          </p>
          <h2 className="text-lg font-semibold">Details</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field error={formState.errors.totalRooms?.message} required>
            <FieldLabel>Total rooms</FieldLabel>
            <FieldInput type="number" min={0} {...register('totalRooms')} />
          </Field>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Section 05
          </p>
          <h2 className="text-lg font-semibold">Social links</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field error={formState.errors.facebookUrl?.message}>
            <FieldLabel>Facebook URL</FieldLabel>
            <FieldInput {...register('facebookUrl')} placeholder="https://facebook.com/…" />
          </Field>

          <Field error={formState.errors.instagramUrl?.message}>
            <FieldLabel>Instagram URL</FieldLabel>
            <FieldInput {...register('instagramUrl')} placeholder="https://instagram.com/…" />
          </Field>

          <Field error={formState.errors.twitterUrl?.message}>
            <FieldLabel>Twitter URL</FieldLabel>
            <FieldInput {...register('twitterUrl')} placeholder="https://x.com/…" />
          </Field>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Section 06
          </p>
          <h2 className="text-lg font-semibold">Property amenities</h2>
          <PermissionGuard permission={PERMISSIONS.AMENITY_CREATE}>
            <CreateAmenityDialog
              onCreated={(amenity) => {
                setAmenityAdditions((prev) => [
                  ...prev,
                  { amenityId: amenity.id, name: amenity.name, isFree: true, isSelected: true },
                ]);
                form.setValue('selectedAmenities', {
                  ...form.getValues('selectedAmenities'),
                  [amenity.id]: true,
                });
                form.setValue('amenityFree', {
                  ...form.getValues('amenityFree'),
                  [amenity.id]: true,
                });
              }}
            >
              <Button variant="outline">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New amenity
              </Button>
            </CreateAmenityDialog>
          </PermissionGuard>
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {amenityList.map((amenity) => {
            const isSelected = watch(`selectedAmenities.${amenity.amenityId}`);
            return (
              <div
                key={amenity.amenityId}
                className="flex items-center justify-between rounded-md border border-border p-3"
              >
                <Field>
                  <Controller
                    control={control}
                    name={`selectedAmenities.${amenity.amenityId}`}
                    defaultValue={false}
                    render={({ field }) => (
                      <FieldCheckbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        onBlur={field.onBlur}
                      >
                        {amenity.name}
                      </FieldCheckbox>
                    )}
                  />
                </Field>

                {isSelected ? (
                  <Field>
                    <Controller
                      control={control}
                      name={`amenityFree.${amenity.amenityId}`}
                      defaultValue={true}
                      render={({ field }) => (
                        <FieldCheckbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          onBlur={field.onBlur}
                        >
                          Free
                        </FieldCheckbox>
                      )}
                    />
                  </Field>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
